import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

/**
 * DepartmentsTab — inline editor for an org's departments and their aliases.
 *
 * Sits inside the People page next to the People tab so org_superadmins (and
 * platform_superusers acting cross-org) have a single Manage-Team surface.
 *
 * SOC 2 CC6.1 — every write hits an audited endpoint:
 *   - rename / aliases  →  PATCH /api/admin/v2/departments/:deptId
 *   - new dept           →  POST  /api/admin/v2/orgs/:orgId/departments/bulk
 *
 * Department deletion is intentionally not exposed yet — dropping a dept that
 * still has assigned users / contract mappings would orphan downstream rows;
 * a dedicated "merge into" flow needs to land before we ship that affordance.
 */
export default function DepartmentsTab({ orgId, departments, onChanged, showToast }) {
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [draftAliases, setDraftAliases] = useState('');
  const [saving, setSaving] = useState(false);

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAliases, setNewAliases] = useState('');

  // Contract / PBP coverage.
  //
  // Mandate refuses any department-bound user whose department is not mapped to
  // a contract AND a PBP, and until now there was no way to choose one: the
  // mapping endpoints existed with no screen in front of them. A department
  // created without coverage silently swallowed every user assigned to it.
  const [contracts, setContracts] = useState([]);
  const [mappedIds, setMappedIds] = useState([]);
  const [newPbps, setNewPbps] = useState([]);          // "contractId::pbpId"
  const [coverageFor, setCoverageFor] = useState(null); // department being mapped
  const [coverageSel, setCoverageSel] = useState([]);

  const loadCoverage = useCallback(async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        fetch(`${API}/api/contracts?org=${orgId}`, { credentials: 'include' }),
        fetch(`${API}/api/departments/contract-mapping-status?org=${orgId}`, { credentials: 'include' }),
      ]);
      const c = await cRes.json();
      const s = await sRes.json();
      if (c.success) setContracts(c.contracts || []);
      if (s.success) setMappedIds(s.mappedDepartmentIds || []);
    } catch { /* the tab still works without coverage data */ }
  }, []);

  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  // Every contract/PBP pair the org has, as flat options.
  const pbpOptions = contracts.flatMap((c) =>
    (c.pbps || []).map((p) => ({ key: `${c.id}::${p}`, contractId: c.id, pbp: p, label: `${c.name} — PBP ${p}` }))
  );

  const toggle = (list, setList, key) =>
    setList(list.includes(key) ? list.filter((x) => x !== key) : [...list, key]);

  const saveCoverage = async (departmentId, selection) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/departments/map-contracts?org=${orgId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          department_id: departmentId,
          mappings: selection.map((k) => {
            const [contract_id, pbp_id] = k.split('::');
            return { contract_id, pbp_id };
          }),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success !== true) {
        showToast('error', 'Coverage NOT saved', data.message || `Server returned ${res.status}`);
      } else {
        showToast('success', 'Coverage saved',
          selection.length === 0
            ? 'No contract selected — users in this department cannot reach Mandate.'
            : `${selection.length} contract/PBP pair(s) assigned.`);
        setCoverageFor(null);
        await loadCoverage();
        onChanged?.();
      }
    } catch (err) {
      showToast('error', 'Network error', err.message);
    }
    setSaving(false);
  };

  const startEdit = (d) => {
    setEditingId(d.id);
    setDraftName(d.name || '');
    setDraftAliases((d.aliases || []).join(', '));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftName('');
    setDraftAliases('');
  };

  const parseAliases = (raw) =>
    raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

  const saveEdit = async () => {
    if (!draftName.trim()) {
      showToast('error', 'Name required', 'Department name cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/v2/departments/${editingId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: draftName.trim(),
          aliases: parseAliases(draftAliases),
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('success', 'Department updated', `${data.department?.name || draftName} saved`);
        cancelEdit();
        onChanged?.();
      } else {
        showToast('error', 'Could not update', data.message || 'Unknown error');
      }
    } catch (err) {
      showToast('error', 'Network error', err.message);
    }
    setSaving(false);
  };

  const submitNew = async () => {
    if (!newName.trim()) {
      showToast('error', 'Name required', 'Department name cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${orgId}/departments/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          departments: [{ name: newName.trim(), aliases: parseAliases(newAliases) }],
        }),
      });
      const data = await res.json();
      if (data.success) {
        const created = data.created?.[0];
        if (created) {
          // Apply the chosen coverage immediately. A department created without
          // it silently rejects every user assigned to it.
          if (newPbps.length > 0) {
            await saveCoverage(created.id, newPbps);
          } else {
            showToast('success', 'Department added', `${created.name} — no contract selected, so its users cannot reach Mandate yet.`);
          }
          setNewPbps([]);
          showToast('success', 'Department added', created.name);
        } else {
          showToast('error', 'Already exists', `A department named "${newName.trim()}" already exists in this org.`);
        }
        setNewName('');
        setNewAliases('');
        setAdding(false);
        onChanged?.();
      } else {
        showToast('error', 'Could not add', data.message || 'Unknown error');
      }
    } catch (err) {
      showToast('error', 'Network error', err.message);
    }
    setSaving(false);
  };

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
        <div>
          <p className="text-sm font-semibold text-white">Departments</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Rename, edit aliases, or add new departments. Aliases drive how Mandate routes incoming regulatory memos —
            list every variation auditors and CMS use for this department.
          </p>
        </div>
        <button
          onClick={() => { setAdding(true); setNewName(''); setNewAliases(''); }}
          className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-sm text-white hover:shadow-lg hover:shadow-blue-500/25 transition flex-shrink-0"
        >
          + Add Department
        </button>
      </div>

      {adding && (
        <div className="px-6 py-5 bg-blue-500/5 border-b border-blue-500/20 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold block mb-1">Name *</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Stars Quality"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold block mb-1">
                Aliases <span className="text-slate-500 normal-case">(comma-separated)</span>
              </label>
              <input
                value={newAliases}
                onChange={(e) => setNewAliases(e.target.value)}
                placeholder="HEDIS, CAHPS, HOS"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Contract coverage. Without at least one pair, every user assigned
              to this department is refused by Mandate. */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold block mb-1">
              Contract &amp; PBP coverage
            </label>
            {pbpOptions.length === 0 ? (
              <p className="text-xs text-amber-300/90">
                This organization has no contract with a PBP yet. Add one under Contracts first —
                until then, users in this department cannot be set up in Mandate.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {pbpOptions.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => toggle(newPbps, setNewPbps, o.key)}
                      className={`px-2.5 py-1 rounded-lg border text-xs transition ${
                        newPbps.includes(o.key)
                          ? 'bg-blue-500/20 border-blue-500 text-blue-200'
                          : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {newPbps.length === 0 && (
                  <p className="mt-1 text-xs text-amber-300/90">
                    Nothing selected. Users in this department will not reach Mandate.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => { setAdding(false); setNewPbps([]); }}
              disabled={saving}
              className="px-3 py-1.5 text-sm text-slate-300 hover:text-white transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submitNew}
              disabled={saving || !newName.trim()}
              className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Department'}
            </button>
          </div>
        </div>
      )}

      {departments.length === 0 ? (
        <div className="px-6 py-12 text-center text-slate-500 text-sm">
          No departments yet. Use “+ Add Department” to create your first one.
        </div>
      ) : (
        <table className="w-full">
          <thead className="bg-slate-900/40 border-b border-slate-700/50">
            <tr>
              <th className="text-left text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-6 py-3 w-1/4">Department</th>
              <th className="text-left text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-4 py-3">Aliases</th>
              <th className="text-right text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-6 py-3 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {departments.map((d) => {
              const isEditing = editingId === d.id;
              return (
                <tr key={d.id} className="border-b border-slate-700/30 hover:bg-slate-700/15 transition align-top">
                  <td className="px-6 py-3">
                    {isEditing ? (
                      <input
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:border-blue-500 focus:outline-none"
                      />
                    ) : (
                      <div className="font-medium text-white text-sm">{d.name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <textarea
                        value={draftAliases}
                        onChange={(e) => setDraftAliases(e.target.value)}
                        rows={2}
                        placeholder="comma, separated, aliases"
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                      />
                    ) : (d.aliases || []).length === 0 ? (
                      <span className="text-slate-600 italic text-xs">No aliases set</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {(d.aliases || []).map((a) => (
                          <span key={a} className="text-[11px] bg-slate-700/60 border border-slate-600 text-slate-200 px-2 py-0.5 rounded-md">
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {isEditing ? (
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="text-xs text-slate-300 hover:text-white transition disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveEdit}
                          disabled={saving || !draftName.trim()}
                          className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-md transition disabled:opacity-50"
                        >
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        {/* Coverage is what decides whether this department's
                            users can exist in Mandate at all, so its state is
                            visible on every row rather than hidden in a form. */}
                        {!mappedIds.includes(d.id) && (
                          <span
                            title="No contract or PBP assigned — users in this department cannot reach Mandate"
                            className="px-2 py-0.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[11px]"
                          >
                            no contract
                          </span>
                        )}
                        <button
                          onClick={() => { setCoverageFor(d); setCoverageSel([]); }}
                          className="px-3 py-1 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 text-slate-200 text-xs font-medium rounded-md transition"
                        >
                          Coverage
                        </button>
                        <button
                          onClick={() => startEdit(d)}
                          className="px-3 py-1 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 text-slate-200 text-xs font-medium rounded-md transition"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {coverageFor && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 flex items-center justify-center p-4"
          onClick={() => setCoverageFor(null)}>
          <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-5"
            onClick={(e) => e.stopPropagation()}>
            <div className="text-white font-semibold">Contract coverage — {coverageFor.name}</div>
            <p className="mt-1 text-xs text-slate-400">
              Mandate refuses any user in this department unless the department is assigned a contract
              and a PBP. Choose every plan this department oversees.
            </p>

            {pbpOptions.length === 0 ? (
              <p className="mt-4 text-sm text-amber-300">
                This organization has no contract with a PBP. Add one under Contracts first.
              </p>
            ) : (
              <div className="mt-4 flex flex-wrap gap-2">
                {pbpOptions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => toggle(coverageSel, setCoverageSel, o.key)}
                    className={`px-2.5 py-1 rounded-lg border text-xs transition ${
                      coverageSel.includes(o.key)
                        ? 'bg-blue-500/20 border-blue-500 text-blue-200'
                        : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-500'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}

            {pbpOptions.length > 0 && (
              <button
                type="button"
                onClick={() => setCoverageSel(coverageSel.length === pbpOptions.length ? [] : pbpOptions.map((o) => o.key))}
                className="mt-2 text-xs text-blue-300 hover:text-blue-200"
              >
                {coverageSel.length === pbpOptions.length ? 'Clear all' : 'Select all plans'}
              </button>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setCoverageFor(null)} disabled={saving}
                className="px-3 py-1.5 text-sm text-slate-300 hover:text-white disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={() => saveCoverage(coverageFor.id, coverageSel)}
                disabled={saving || pbpOptions.length === 0}
                className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save coverage'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
